import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Download, Save, Sparkles, Wand2, Type, WandSparkles } from "lucide-react";
import IconGalleryGrid from "./IconGalleryGrid";

const API = getApiUrl();

const AI_MODELS = [
  { value: "ideogram_4", label: "Ideogram 4 — best for crisp logo/icon art & in-image text" },
  { value: "gpt_image_2", label: "GPT Image 2 — strong general-purpose quality" },
  { value: "nano_banana_pro", label: "Nano Banana Pro — great photorealistic/stylised results" },
  { value: "flux_2", label: "FLUX.2 — fast, versatile style range" },
];

function roundedRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Draws the shared square/circular frame (background or clipped AI image,
// border, drop shadow) — `image` is an optional loaded <img> element;
// `placeholder` draws an empty neutral frame (AI mode before generating).
function drawIcon(canvas, opts, image, placeholder) {
  const { size, text, fgColor, bgColor, borderSize, borderColor, cornerRadius, shadow } = opts;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);

  const radius = Math.min(size / 2, (cornerRadius / 50) * (size / 2));
  const inset = borderSize / 2;

  ctx.save();
  if (shadow && !placeholder) {
    ctx.shadowColor = "rgba(0,0,0,0.35)";
    ctx.shadowBlur = size * 0.08;
    ctx.shadowOffsetY = size * 0.03;
  }
  roundedRectPath(ctx, inset, inset, size - inset * 2, size - inset * 2, Math.max(0, radius - inset));
  if (image) {
    ctx.fillStyle = "#ffffff";
  } else if (placeholder) {
    ctx.fillStyle = "#e5e7eb";
  } else {
    ctx.fillStyle = bgColor;
  }
  ctx.fill();
  ctx.restore();

  if (image) {
    ctx.save();
    roundedRectPath(ctx, inset, inset, size - inset * 2, size - inset * 2, Math.max(0, radius - inset));
    ctx.clip();
    // cover-fit the AI image into the square frame
    const scale = Math.max(size / image.width, size / image.height);
    const w = image.width * scale;
    const h = image.height * scale;
    ctx.drawImage(image, (size - w) / 2, (size - h) / 2, w, h);
    ctx.restore();
  }

  if (borderSize > 0 && !placeholder) {
    roundedRectPath(ctx, inset, inset, size - inset * 2, size - inset * 2, Math.max(0, radius - inset));
    ctx.lineWidth = borderSize;
    ctx.strokeStyle = borderColor;
    ctx.stroke();
  }

  if (!image && !placeholder) {
    const label = (text || "").trim();
    if (label) {
      ctx.fillStyle = fgColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      let fontSize = size * (label.length > 2 ? 0.32 : 0.42);
      ctx.font = `700 ${fontSize}px 'Inter', 'Segoe UI', sans-serif`;
      const maxWidth = size * 0.78;
      while (ctx.measureText(label).width > maxWidth && fontSize > 4) {
        fontSize -= 1;
        ctx.font = `700 ${fontSize}px 'Inter', 'Segoe UI', sans-serif`;
      }
      ctx.fillText(label, size / 2, size / 2 + fontSize * 0.04);
    }
  }
}

export default function IconGeneratorAdmin() {
  const [mode, setMode] = useState("text"); // "text" | "ai"
  const [text, setText] = useState("AB");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiModel, setAiModel] = useState("ideogram_4");
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiImageEl, setAiImageEl] = useState(null);
  const [name, setName] = useState("");
  const [size, setSize] = useState(128);
  const [fgColor, setFgColor] = useState("#ffffff");
  const [bgColor, setBgColor] = useState("#2f4f4f");
  const [borderSize, setBorderSize] = useState(0);
  const [borderColor, setBorderColor] = useState("#1a1a1a");
  const [cornerRadius, setCornerRadius] = useState(20);
  const [shadow, setShadow] = useState(false);
  const [saveToGallery, setSaveToGallery] = useState(true);
  const [saving, setSaving] = useState(false);
  const [galleryRefreshToken, setGalleryRefreshToken] = useState(0);
  const [enhancingPrompt, setEnhancingPrompt] = useState(false);
  const canvasRef = useRef(null);

  const config = { size, text, fgColor, bgColor, borderSize, borderColor, cornerRadius, shadow };
  const aiNeedsArtwork = mode === "ai" && !aiImageEl;

  useEffect(() => {
    if (canvasRef.current) drawIcon(canvasRef.current, config, mode === "ai" ? aiImageEl : null, aiNeedsArtwork);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, size, fgColor, bgColor, borderSize, borderColor, cornerRadius, shadow, mode, aiImageEl]);

  const handleGenerateAi = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Describe the icon you want first");
      return;
    }
    setGeneratingAi(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/icons/generate-ai`,
        { prompt: aiPrompt.trim(), model: aiModel },
        { withCredentials: true }
      );
      const img = new Image();
      img.onload = () => setAiImageEl(img);
      img.src = data.data_url;
      toast.success("Icon artwork generated");
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Insufficient Universal Key credits for AI image generation");
      } else {
        toast.error(err?.response?.data?.detail || "AI generation failed");
      }
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleEnhancePrompt = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Type a rough idea first");
      return;
    }
    setEnhancingPrompt(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/icons/enhance-prompt`,
        { prompt: aiPrompt.trim() },
        { withCredentials: true }
      );
      if (data.enhanced_prompt) {
        setAiPrompt(data.enhanced_prompt);
        toast.success("Prompt enhanced with Claude");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to enhance prompt");
    } finally {
      setEnhancingPrompt(false);
    }
  };

  const getBlob = () =>
    new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      drawIcon(canvas, config, mode === "ai" ? aiImageEl : null);
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });

  const handleDownload = async () => {
    if (aiNeedsArtwork) {
      toast.error("Generate the AI artwork first");
      return;
    }
    const blob = await getBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || text || "icon").trim().replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const handleCreate = async () => {
    if (mode === "text" && !text.trim()) {
      toast.error("Enter some text for the icon first");
      return;
    }
    if (mode === "ai" && !aiImageEl) {
      toast.error("Generate the AI artwork first");
      return;
    }
    if (saveToGallery && !name.trim()) {
      toast.error("Give this icon a name to save it to the gallery");
      return;
    }
    setSaving(true);
    try {
      if (!saveToGallery) {
        await handleDownload();
        toast.success("Icon downloaded");
        return;
      }
      const blob = await getBlob();
      const formData = new FormData();
      formData.append("file", blob, `${name.trim().replace(/\s+/g, "-").toLowerCase()}.png`);
      const { data: uploadData } = await axios.post(`${API}/admin/upload?skip_watermark=true`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!uploadData.url) throw new Error("Upload failed");
      await axios.post(
        `${API}/admin/icons`,
        { name: name.trim(), image_url: uploadData.url, ...config, source: mode },
        { withCredentials: true }
      );
      toast.success("Icon saved to gallery");
      setName("");
      setGalleryRefreshToken((t) => t + 1);
    } catch {
      toast.error("Failed to save icon");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="icon-generator-admin">
      <div>
        <h3 className="text-xl font-semibold flex items-center gap-2"><Sparkles size={18} /> Icon Generator</h3>
        <p className="text-sm text-muted-foreground">Turn text or an AI prompt into a styled icon, then save it to the Icon Gallery for reuse anywhere an image URL is needed — including as the site favicon.</p>
      </div>

      <div className="flex gap-2 border-b">
        <button type="button" onClick={() => setMode("text")}
          className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${mode === "text" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="icon-generator-mode-text">
          <Type size={14} /> Text
        </button>
        <button type="button" onClick={() => setMode("ai")}
          className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${mode === "ai" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="icon-generator-mode-ai">
          <Wand2 size={14} /> AI Image
        </button>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-8">
        <div className="flex flex-col items-center gap-3">
          <div
            className="flex items-center justify-center border border-dashed border-border bg-muted/30 p-4"
            style={{ width: 220, height: 220 }}
          >
            <canvas
              ref={canvasRef}
              style={{ maxWidth: "100%", maxHeight: "100%", width: Math.min(size, 180), height: Math.min(size, 180) }}
              data-testid="icon-generator-canvas"
            />
          </div>
          <p className="text-xs text-muted-foreground">{size}x{size}px preview</p>
        </div>

        <div className="space-y-4 max-w-xl">
          {mode === "text" ? (
            <div>
              <Label className="text-xs font-medium">Icon Text</Label>
              <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={6} placeholder="e.g. AB" data-testid="icon-generator-text-input" />
            </div>
          ) : (
            <div className="space-y-3 p-3 border border-border bg-muted/20">
              <div>
                <Label className="text-xs font-medium">AI Model</Label>
                <Select value={aiModel} onValueChange={setAiModel}>
                  <SelectTrigger data-testid="icon-generator-ai-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AI_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Describe the icon artwork</Label>
                  <button type="button" onClick={handleEnhancePrompt} disabled={enhancingPrompt}
                    className="text-xs text-primary hover:underline flex items-center gap-1 disabled:opacity-50"
                    data-testid="icon-generator-enhance-prompt-btn">
                    {enhancingPrompt ? <Loader2 size={12} className="animate-spin" /> : <WandSparkles size={12} />}
                    Enhance with Claude
                  </button>
                </div>
                <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g. a minimalist house roofline logo on a flat background"
                  rows={3} data-testid="icon-generator-ai-prompt-input" />
              </div>
              <Button type="button" onClick={handleGenerateAi} disabled={generatingAi} data-testid="icon-generator-ai-generate-btn">
                {generatingAi ? <Loader2 size={16} className="animate-spin mr-2" /> : <Wand2 size={16} className="mr-2" />}
                Generate artwork
              </Button>
              <p className="text-xs text-muted-foreground">The border, corner radius and shadow controls below are applied on top of the generated artwork.</p>
            </div>
          )}


          <div>
            <Label className="text-xs font-medium">Name (used for saving / filename)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Agent Badge Icon" data-testid="icon-generator-name-input" />
          </div>

          <div>
            <Label className="text-xs font-medium">Icon Size: {size}px</Label>
            <input type="range" min="32" max="512" step="8" value={size}
              onChange={(e) => setSize(parseInt(e.target.value))} className="w-full" data-testid="icon-generator-size-input" />
          </div>

          {mode === "text" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium">Foreground Colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} className="h-9 w-9 border border-input" data-testid="icon-generator-fg-color" />
                  <Input value={fgColor} onChange={(e) => setFgColor(e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Background Colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="h-9 w-9 border border-input" data-testid="icon-generator-bg-color" />
                  <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="text-xs" />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium">Border Size: {borderSize}px</Label>
              <input type="range" min="0" max="20" value={borderSize}
                onChange={(e) => setBorderSize(parseInt(e.target.value))} className="w-full" data-testid="icon-generator-border-size" />
            </div>
            <div>
              <Label className="text-xs font-medium">Border Colour</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={borderColor} onChange={(e) => setBorderColor(e.target.value)} className="h-9 w-9 border border-input" data-testid="icon-generator-border-color" />
                <Input value={borderColor} onChange={(e) => setBorderColor(e.target.value)} className="text-xs" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium">Corner Radius: {cornerRadius}% {cornerRadius >= 50 ? "(circle)" : cornerRadius === 0 ? "(square)" : "(rounded)"}</Label>
            <input type="range" min="0" max="50" value={cornerRadius}
              onChange={(e) => setCornerRadius(parseInt(e.target.value))} className="w-full" data-testid="icon-generator-corner-radius" />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={shadow} onCheckedChange={setShadow} data-testid="icon-generator-shadow-toggle" />
            <Label className="text-xs font-medium">Drop shadow</Label>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <Switch checked={saveToGallery} onCheckedChange={setSaveToGallery} data-testid="icon-generator-save-toggle" />
            <Label className="text-xs font-medium">Store in Icon Gallery (off = download only)</Label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleCreate} disabled={saving} data-testid="icon-generator-create-btn">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : saveToGallery ? <Save size={16} className="mr-2" /> : <Download size={16} className="mr-2" />}
              {saveToGallery ? "Create & Save to Gallery" : "Create & Download"}
            </Button>
            {saveToGallery && (
              <Button variant="outline" onClick={handleDownload} data-testid="icon-generator-download-btn">
                <Download size={16} className="mr-2" /> Download only
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <IconGalleryGrid refreshToken={galleryRefreshToken} />
      </div>
    </div>
  );
}
